import React, { useState } from 'react';
import { X, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = [
  { type: 'solo_ingresso',                       label: 'Solo Ingresso',                              price: '€ 10' },
  { type: 'ingresso_guardaroba',                 label: 'Ingresso + Guardaroba',                      price: '€ 15' },
  { type: 'ingresso_tavolo_bottiglia',           label: 'Ingresso + Tavolo e Bottiglia',              price: '€ 40' },
  { type: 'ingresso_tavolo_bottiglia_guardaroba',label: 'Ingresso + Tavolo, Bottiglia e Guardaroba',  price: '€ 50' },
];

function formatDate(dateStr) {
  if (!dateStr) return '';
  try { return new Date(dateStr.split(' ')[0]).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return dateStr; }
}

export default function BookingModify() {
  const [code, setCode]       = useState('');
  const [booking, setBooking] = useState(null);
  const [form, setForm]       = useState({});
  const [step, setStep]       = useState('search'); // search | edit | success
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleSearch = async () => {
    if (!code.trim()) return;
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/bookings/${code.trim().toUpperCase()}`);
      setBooking(res.data);
      setForm({
        first_name: res.data.first_name,
        last_name:  res.data.last_name,
        email:      res.data.email || '',
        phone:      res.data.phone || '',
        num_people: res.data.num_people,
        package_type: res.data.package_type,
        package_label: res.data.package_label,
      });
      setStep('edit');
    } catch {
      setError('Prenotazione non trovata. Controlla il codice e riprova.');
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    setLoading(true); setError('');
    try {
      const pkg = PACKAGES.find(p => p.type === form.package_type);
      await axios.put(`${API}/bookings/${booking.booking_code}`, {
        ...form,
        package_label: pkg?.label || form.package_label,
        num_people: Number(form.num_people),
      });
      setStep('success');
    } catch {
      setError('Errore durante la modifica. Riprova.');
    } finally { setLoading(false); }
  };

  const reset = () => { setCode(''); setBooking(null); setForm({}); setStep('search'); setError(''); };

  return (
    <section id="modifica-prenotazione" data-testid="booking-modify-section" className="py-16" style={{ background: 'var(--bg-primary)' }}>
      <div className="max-w-2xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <p className="section-eyebrow">Gestisci la tua prenotazione</p>
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-3">
            Modifica o Disdici
          </h2>
          <p className="text-gray-400 text-lg">Inserisci il codice della tua prenotazione per modificarla.</p>
        </div>

        {/* Cancellation policy */}
        <div
          className="flex items-start gap-3 p-4 rounded-xl mb-8"
          style={{ background: 'rgba(79,195,212,0.06)', border: '1px solid rgba(79,195,212,0.2)' }}
        >
          <AlertCircle size={22} className="text-aqua shrink-0 mt-0.5" />
          <div>
            <p className="text-white font-600 font-manrope text-base">Politica di cancellazione</p>
            <p className="text-gray-400 text-sm mt-1">
              Puoi annullare gratuitamente fino a <strong className="text-white">24 ore prima</strong> dell'evento. Per annullare contatta il 351 889 2962 con il tuo codice prenotazione. Non essendoci pagamento anticipato, non verrà addebitato nulla.
            </p>
          </div>
        </div>

        <div className="card-dark p-6 md:p-8">
          {/* STEP: Search */}
          {step === 'search' && (
            <div data-testid="modify-search">
              <label className="block text-lg font-manrope font-600 text-gray-200 mb-3">
                Codice prenotazione
              </label>
              <div className="flex gap-3">
                <input
                  data-testid="modify-code-input"
                  type="text"
                  placeholder="LP-XXXXXXXX"
                  value={code}
                  onChange={e => setCode(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  className="input-elegant flex-1"
                  style={{ fontSize: '1.2rem', height: '60px', letterSpacing: '0.1em' }}
                />
                <button
                  data-testid="modify-search-btn"
                  onClick={handleSearch}
                  disabled={loading || !code.trim()}
                  className="btn-primary px-6"
                  style={{ minHeight: '60px' }}
                >
                  {loading ? '...' : 'Cerca'}
                </button>
              </div>
              {error && <p className="text-red-400 mt-3 font-manrope">{error}</p>}
            </div>
          )}

          {/* STEP: Edit */}
          {step === 'edit' && booking && (
            <div data-testid="modify-edit">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-gold font-manrope font-700 text-sm uppercase tracking-wider">Prenotazione trovata</p>
                  <p className="font-playfair text-xl font-bold text-white mt-0.5">{booking.booking_code}</p>
                </div>
                <span className="text-gray-400 text-sm font-manrope">{booking.event_title}</span>
              </div>
              <div className="divider-gold" />

              <div className="space-y-5 mt-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-base font-manrope font-600 text-gray-300 mb-1.5">Nome</label>
                    <input type="text" value={form.first_name || ''} onChange={e => setForm(f => ({...f, first_name: e.target.value}))}
                      className="input-elegant" style={{ height: '56px', fontSize: '1.1rem' }} />
                  </div>
                  <div>
                    <label className="block text-base font-manrope font-600 text-gray-300 mb-1.5">Cognome</label>
                    <input type="text" value={form.last_name || ''} onChange={e => setForm(f => ({...f, last_name: e.target.value}))}
                      className="input-elegant" style={{ height: '56px', fontSize: '1.1rem' }} />
                  </div>
                </div>
                <div>
                  <label className="block text-base font-manrope font-600 text-gray-300 mb-1.5">Telefono</label>
                  <input type="tel" value={form.phone || ''} onChange={e => setForm(f => ({...f, phone: e.target.value}))}
                    className="input-elegant" style={{ height: '56px', fontSize: '1.1rem' }} placeholder="351 000 0000" />
                </div>
                <div>
                  <label className="block text-base font-manrope font-600 text-gray-300 mb-1.5">Numero di persone</label>
                  <div className="flex items-center gap-4">
                    <button onClick={() => setForm(f => ({...f, num_people: Math.max(1, (f.num_people||1)-1)}))}
                      className="w-14 h-14 rounded-full border-2 border-gold text-gold flex items-center justify-center text-2xl hover:bg-gold/10">−</button>
                    <span className="font-playfair text-4xl font-bold text-white w-16 text-center">{form.num_people}</span>
                    <button onClick={() => setForm(f => ({...f, num_people: Math.min(20, (f.num_people||1)+1)}))}
                      className="w-14 h-14 rounded-full border-2 border-gold text-gold flex items-center justify-center text-2xl hover:bg-gold/10">+</button>
                  </div>
                </div>
                <div>
                  <label className="block text-base font-manrope font-600 text-gray-300 mb-2">Pacchetto</label>
                  <div className="grid grid-cols-1 gap-2">
                    {PACKAGES.map(pkg => (
                      <button key={pkg.type} onClick={() => setForm(f => ({...f, package_type: pkg.type, package_label: pkg.label}))}
                        className={`card-booking text-left py-3 px-4 flex items-center justify-between ${form.package_type === pkg.type ? 'selected' : ''}`}>
                        <span className="font-manrope text-base text-white">{pkg.label}</span>
                        <span className="text-gold font-bold font-playfair">{pkg.price}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {error && <p className="text-red-400 mt-4 font-manrope">{error}</p>}

              <div className="flex gap-3 mt-8">
                <button onClick={reset} className="btn-secondary flex-1 justify-center">Annulla</button>
                <button data-testid="save-modify-btn" onClick={handleSave} disabled={loading}
                  className="btn-primary flex-1 justify-center">
                  {loading ? 'Salvataggio...' : 'Salva modifiche'}
                </button>
              </div>
            </div>
          )}

          {/* STEP: Success */}
          {step === 'success' && (
            <div data-testid="modify-success" className="text-center py-6">
              <CheckCircle size={56} className="text-gold mx-auto mb-4" />
              <h3 className="font-playfair text-2xl font-bold text-white mb-2">Prenotazione aggiornata!</h3>
              <p className="text-gray-400 text-lg mb-6">Le tue modifiche sono state salvate correttamente.</p>
              <button onClick={reset} className="btn-primary mx-auto">Chiudi</button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
